import { useUpdateEntity } from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import React from 'react'
import { CheckSquareIcon } from 'src/editor/shared/icons/check-square-icon'
import { Toggle } from 'src/editor/shared/ui-lib/toggle'
import { getContactSlugName } from 'src/editor/shared/utils/get-contact-field-title'
import {
  createFormCheckboxSubEntity,
  createFormFieldSubEntity,
} from 'src/shared/entities/form/create-form'
import { FormSubEntityTypeEnum } from 'src/shared/entities/form/enums'
import {
  FormInterface,
  FormSubEntitiesInterface,
} from 'src/shared/entities/form/form.schema'
import { ContactSlugEnum } from 'src/shared/enums/contact-fields-enum'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { getFormSubEntitiesSettingsIcons } from '../../../utils/get-form-sub-entities-settings-icons'
import { HORIZONTAL_LINE_INDEXES } from './constants'
import { FormAddNewSubEntitySettingsItem } from './form-add-new-sub-entity-settings-item'
import { FormSubEntityNumberInput } from './form-sub-entity-number-input'
import { formAddNewSubEntitySettingsList } from './formAddNewSubEntitySettingsList'

interface FormAddNewSubEntitySettingsProps {
  entity: FormInterface
}

const HorizontalLine = () => <hr className="my-2 text-divider00" />

export const FormAddNewSubEntitySettings = ({
  entity,
}: FormAddNewSubEntitySettingsProps) => {
  const { t } = useTypedTranslation()

  const updateEntity = useUpdateEntity<FormInterface>()

  const getIsExisted = (slug: ContactSlugEnum) => {
    return entity.subEntities.some(
      subEntity =>
        subEntity.type === FormSubEntityTypeEnum.Field &&
        subEntity.slug === slug,
    )
  }

  const onToggle = (slug: ContactSlugEnum) => {
    const isExisted = getIsExisted(slug)
    if (isExisted) {
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.filter(
          subEntity =>
            subEntity.type !== FormSubEntityTypeEnum.Field ||
            subEntity.slug !== slug,
        ),
      })
    } else {
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.reduce((acc, subEntity, index) => {
          // NOTE: Add the new field before the submit button
          if (index === entity.subEntities.length - 1) {
            acc.push(createFormFieldSubEntity({ slug, t }))
          }
          acc.push(subEntity)
          return acc
        }, [] as FormSubEntitiesInterface[]),
      })
    }
  }

  const checkboxes = entity.subEntities.filter(
    subEntity => subEntity.type === FormSubEntityTypeEnum.Checkbox,
  )

  const onChangeNumberOfCheckboxes = (value: number) => {
    if (value > checkboxes.length) {
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.reduce((acc, subEntity, index) => {
          // NOTE: Add the new checkbox before the submit button
          if (index === entity.subEntities.length - 1) {
            acc.push(createFormCheckboxSubEntity({ t }))
          }
          acc.push(subEntity)
          return acc
        }, [] as FormSubEntitiesInterface[]),
      })
    } else {
      const checkboxesToRemove = checkboxes[checkboxes.length - 1] // NOTE: Remove the last checkbox
      updateEntity({
        ...entity,
        subEntities: entity.subEntities.filter(
          subEntity => subEntity.id !== checkboxesToRemove.id,
        ),
      })
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {formAddNewSubEntitySettingsList.map((slug, index) => (
        <>
          {HORIZONTAL_LINE_INDEXES.includes(index) && <HorizontalLine />}
          {
            <FormAddNewSubEntitySettingsItem
              key={slug}
              title={getContactSlugName(slug, t)}
              icon={getFormSubEntitiesSettingsIcons(slug)}
              controlComponent={() => (
                <Toggle
                  onChange={() => onToggle(slug)}
                  checked={getIsExisted(slug)}
                />
              )}
            />
          }
        </>
      ))}
      <HorizontalLine />
      <FormAddNewSubEntitySettingsItem
        title={t('entity_settings.checkbox.header')}
        icon={CheckSquareIcon}
        controlComponent={() => (
          <FormSubEntityNumberInput
            value={checkboxes.length}
            onChange={onChangeNumberOfCheckboxes}
          />
        )}
      />
    </div>
  )
}
